import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';
import type {
  ExecutionStatusKey,
  MetricsApiDto,
  MetricsRange,
  MetricsScope,
} from '@/features/metrics';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

type MetricsQueryInput = {
  range: MetricsRange;
  scope: MetricsScope;
  statuses?: ExecutionStatusKey[];
  tags?: string[];
  suiteIds?: string[];
  projectId?: string | null;
  entityId?: string | null;
  entityIds?: string[];
  name?: string | null;
  day?: string | null;
  startDay?: string | null;
  endDay?: string | null;
  latestOnly?: boolean;
};

type MetricsBatchQueryInput = {
  range: MetricsRange;
  scope: MetricsScope;
  statuses?: ExecutionStatusKey[];
  tags?: string[];
  suiteIds?: string[];
  name?: string | null;
  startDay?: string | null;
  endDay?: string | null;
  latestOnly?: boolean;
  entities: Array<{ entityId: string; projectId?: string | null }>;
};

type MetricsBatchPayload = Omit<MetricsBatchQueryInput, 'startDay' | 'endDay'> & {
  startDay?: string;
  endDay?: string;
};

type MetricsBatchResultItem = {
  entityId: string;
  projectId: string | null;
  metrics: MetricsApiDto;
};

type MetricsTagFacetsQueryInput = Omit<MetricsQueryInput, 'tags' | 'day'> & {
  tags: string[];
};

type MetricsTagFacetsPayload = Omit<MetricsTagFacetsQueryInput, 'startDay' | 'endDay'> & {
  startDay?: string;
  endDay?: string;
};

type MetricsTagFacetCount = {
  count: number;
  tag: string;
};

export const getMetrics = async (
  input: MetricsQueryInput,
  organisationId?: string | null,
): Promise<MetricsApiDto> => {
  const token = ensureToken();
  const params = new URLSearchParams();
  params.set('range', input.range);
  params.set('scope', input.scope);
  if (input.projectId) {
    params.set('projectId', input.projectId);
  }
  if (input.entityId) {
    params.set('entityId', input.entityId);
  }
  if (input.entityIds && input.entityIds.length > 0) {
    params.set('entityIds', input.entityIds.join(','));
  }
  if (input.statuses && input.statuses.length > 0) {
    params.set('statuses', input.statuses.join(','));
  }
  if (input.tags && input.tags.length > 0) {
    params.set('tags', input.tags.join(','));
  }
  if (input.suiteIds && input.suiteIds.length > 0) {
    params.set('suiteIds', input.suiteIds.join(','));
  }
  if (input.name) {
    params.set('name', input.name);
  }
  if (input.day) {
    params.set('day', input.day);
  }
  if (input.startDay && input.endDay) {
    params.set('startDay', input.startDay);
    params.set('endDay', input.endDay);
  }
  if (input.latestOnly) {
    params.set('latestOnly', 'true');
  }
  const query = params.toString();
  const path = query.length > 0 ? `execution-metrics?${query}` : 'execution-metrics';
  try {
    return await apiClient.get<MetricsApiDto>(path, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
      ignoreOrgRemoval: true,
    });
  } catch (err) {
    throw toApiError(err);
  }
};

export const getMetricsBatch = async (
  input: MetricsBatchQueryInput,
  organisationId?: string | null,
): Promise<MetricsBatchResultItem[]> => {
  const token = ensureToken();
  const { startDay, endDay, ...basePayload } = input;
  const payload: MetricsBatchPayload =
    startDay && endDay
      ? {
          ...basePayload,
          startDay,
          endDay,
        }
      : basePayload;
  try {
    return await apiClient.post<MetricsBatchResultItem[]>('execution-metrics/batch', payload, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
      ignoreOrgRemoval: true,
    });
  } catch (err) {
    throw toApiError(err);
  }
};

export const getMetricsTagFacets = async (
  input: MetricsTagFacetsQueryInput,
  organisationId?: string | null,
): Promise<MetricsTagFacetCount[]> => {
  const token = ensureToken();
  const { startDay, endDay, ...basePayload } = input;
  const payload: MetricsTagFacetsPayload =
    startDay && endDay
      ? {
          ...basePayload,
          startDay,
          endDay,
        }
      : basePayload;
  try {
    return await apiClient.post<MetricsTagFacetCount[]>('execution-metrics/tag-facets', payload, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
      ignoreOrgRemoval: true,
    });
  } catch (err) {
    throw toApiError(err);
  }
};
